// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Thin, uniform wrappers around hash-wasm. Every algorithm is exposed through
 * the same incremental interface so the streaming worker and the one-shot text
 * paths share a single code path. hash-wasm bundles its own WebAssembly inline,
 * so nothing is fetched at runtime and everything works offline.
 */
import {
  createBLAKE2b,
  createBLAKE3,
  createCRC32,
  createHMAC,
  createMD5,
  createSHA1,
  createSHA256,
  createSHA3,
  createSHA512,
  createXXHash64,
  type IHasher,
} from 'hash-wasm';
import type { AlgoId, HashSet } from './types';

type Factory = () => Promise<IHasher>;

const FACTORIES: Record<AlgoId, Factory> = {
  crc32: () => createCRC32(),
  md5: () => createMD5(),
  sha1: () => createSHA1(),
  sha256: () => createSHA256(),
  sha512: () => createSHA512(),
  'sha3-256': () => createSHA3(256),
  'blake2b-256': () => createBLAKE2b(256),
  blake3: () => createBLAKE3(256),
  xxh64: () => createXXHash64(),
};

/** Algorithms that form a valid HMAC construction (block-based, keyed). */
export const HMAC_ALGOS: AlgoId[] = ['sha256', 'sha512', 'sha1', 'md5', 'sha3-256'];

/** Create a single initialised incremental hasher. */
export async function createHasher(algo: AlgoId): Promise<IHasher> {
  const h = await FACTORIES[algo]();
  h.init();
  return h;
}

export interface MultiHasher {
  readonly algos: AlgoId[];
  update(chunk: Uint8Array): void;
  finalize(): HashSet;
}

/** Fan one byte-stream out into several algorithms at once. */
export async function createMultiHasher(algos: AlgoId[]): Promise<MultiHasher> {
  const entries = await Promise.all(
    algos.map(async (a) => [a, await createHasher(a)] as const),
  );
  return {
    algos,
    update(chunk: Uint8Array) {
      for (const [, h] of entries) h.update(chunk);
    },
    finalize(): HashSet {
      const out: HashSet = {};
      for (const [a, h] of entries) out[a] = h.digest('hex');
      return out;
    },
  };
}

/** One-shot digest of an in-memory buffer for a single algorithm. */
export async function hashBytes(algo: AlgoId, data: Uint8Array): Promise<string> {
  const h = await createHasher(algo);
  h.update(data);
  return h.digest('hex');
}

/** One-shot digests of an in-memory buffer across several algorithms. */
export async function hashAll(algos: AlgoId[], data: Uint8Array): Promise<HashSet> {
  const m = await createMultiHasher(algos);
  m.update(data);
  return m.finalize();
}

/** Keyed HMAC digest (hex). Throws for algorithms outside {@link HMAC_ALGOS}. */
export async function hmacHex(
  algo: AlgoId,
  key: Uint8Array,
  message: Uint8Array,
): Promise<string> {
  if (!HMAC_ALGOS.includes(algo)) {
    throw new Error(`${algo} is not a valid HMAC hash`);
  }
  const h = await createHMAC(FACTORIES[algo](), key);
  h.init();
  h.update(message);
  return h.digest('hex');
}
